import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

/**
 * Signals data-wiring helpers. These keep each panel honest about whether it is
 * showing LIVE backend data or the bundled SAMPLE fixtures, without touching the
 * shared design system. Provenance for first-party analytics is "via aigen
 * analytics" (ProvenanceTag in @/ui only knows posthog/matomo, so the first-
 * party label is rendered here with the identical mono styling).
 */

/** Tiny mono "sample" chip — shown whenever a panel falls back to fixtures. */
export function SampleChip({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-chip bg-paper-2 px-1.5 py-0.5 font-mono text-[10px] lowercase tracking-wide text-subtle dark:bg-white/5",
        className,
      )}
      title="Bundled sample data — not connected to your live property"
    >
      sample
    </span>
  );
}

/** First-party provenance label (matches ProvenanceTag styling). */
export function AigenTag({ className }: { className?: string }) {
  return (
    <span className={cn("font-mono text-[10px] lowercase tracking-wide text-subtle", className)}>
      via aigen analytics
    </span>
  );
}

/** Quiet "demo" note for seeded backend rows (real endpoint, synthetic data). */
export function DemoNote({ className }: { className?: string }) {
  return (
    <span
      className={cn("font-mono text-[10px] lowercase tracking-wide text-subtle", className)}
      title="This property is seeded with demo data on the backend"
    >
      demo
    </span>
  );
}

/** A calm card-height skeleton block (no layout shift). */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-card bg-paper-2 dark:bg-white/5", className)} />;
}

/** Centered, quiet empty state for a connected-but-empty property. */
export function EmptyState({
  title = "No data yet",
  detail,
  className,
}: {
  title?: ReactNode;
  detail?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("grid place-items-center rounded-card border border-dashed border-hairline px-6 py-12 text-center", className)}>
      <div className="max-w-xs">
        <p className="text-[14px] font-medium text-ink-strong">{title}</p>
        {detail && <p className="mt-1 text-[13px] leading-relaxed text-muted">{detail}</p>}
      </div>
    </div>
  );
}

/**
 * Resolve a panel's data state. `live` is true only when the workspace is backed
 * by a real site AND the query has resolved without error. When not live we keep
 * showing the bundled sample (marked with <SampleChip/>).
 */
export type PanelState = "loading" | "live" | "empty" | "sample";

export function panelState(args: {
  siteId: string | undefined;
  workspaceLive: boolean;
  isLoading: boolean;
  isError: boolean;
  hasData: boolean;
}): PanelState {
  const { siteId, workspaceLive, isLoading, isError, hasData } = args;
  // No real property selected, or whole workspace is on mock fallback → sample.
  if (!siteId || !workspaceLive) return "sample";
  if (isLoading) return "loading";
  if (isError) return "sample"; // 401 / network → graceful fallback to fixtures
  if (!hasData) return "empty";
  return "live";
}

/** Short time label from an epoch-ms timestamp, range-aware. */
export function timeLabel(ms: number, range: string): string {
  const d = new Date(ms);
  if (range === "24h") {
    return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
  }
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** "8m ago" style relative label from epoch ms. */
export function relativeTime(ms: number): string {
  const diff = Math.max(0, Date.now() - ms);
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

/** mm:ss / m:ss duration from seconds. */
export function durationLabel(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}
